import { useForm } from "react-hook-form";
import {
  Stack,
  Button,
  Title,
  Paper,
  Container,
  Text,
  Alert,
  Box,
  Grid,
  Divider,
  Badge,
  Select as MantineSelect,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { TextInput, Textarea, Select } from "react-hook-form-mantine";
import resolver from "@congrs/resolver";
import {
  createGiveawaySchema,
  web_updateGiveawaySchema,
} from "@congrs/schemas";
import {
  useCreateGiveaway,
  useUpdateGiveaway,
  useUpdateGiveawayStatus,
  useDeleteGiveaway,
  useGetGiveawayStats,
} from "../../api/giveaways";

import LocalizedInput from "../common/LocalizedInput";
import { extractDirtyData } from "../../utils/dirtyData";
import { useConfirm } from "../../hooks/useConfirm";
import {
  makeI18nDefaultValue,
  mergeI18nWithDefaults,
} from "../../utils/i18nUtils";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";

const STATUS_INFO = {
  draft: {
    label: "Draft",
    color: "gray",
    description: "Users are not able to see giveaway",
  },
  published: {
    label: "Published",
    color: "blue",
    description:
      "Users can see all the giveaway information, but they cannot scan and add to their progress",
  },
  live: {
    label: "Live",
    color: "green",
    description: "Users can scan sponsor codes and add to their progress",
  },
  closed: {
    label: "Closed",
    color: "red",
    description: "Users cannot scan and add to their progress anymore",
  },
};

const getTransitionWarning = (fromStatus, toStatus, hasScans) => {
  if (fromStatus === toStatus) {
    return null;
  }

  if (
    fromStatus === "live" &&
    ["draft", "published"].includes(toStatus) &&
    hasScans
  ) {
    return {
      title: "Change Giveaway Status?",
      message:
        "Giveaway has scans, users can only scan sponsors while giveaway is in active status. Changing the status back to published or draft will cause users not being able to scan anymore. Are you sure?",
    };
  }

  if (fromStatus === "closed" && toStatus === "live") {
    return {
      title: "Reopen Giveaway?",
      message:
        "Users will be able to continue scanning sponsors when status is changed to live. Are you sure?",
    };
  }

  return null;
};

const getStatusBadgeProps = (status) => {
  const info = STATUS_INFO[status];
  return info
    ? { color: info.color, label: info.label }
    : { color: "gray", label: status };
};

const localizedInputFields = [
  {
    nameBase: "titleI18n",
    label: "Title",
    component: TextInput,
    props: {
      size: "sm",
      placeholder: "Giveaway title",
      withAsterisk: true,
      autosize: "true",
    },
  },
  {
    nameBase: "descriptionI18n",
    label: "Description",
    component: Textarea,
    props: {
      autosize: true,
      minRows: 4,
      size: "sm",
      placeholder: "Giveaway description",
    },
  },
];

export default function GiveawayForm({
  giveaway = null,
  event = null,
  pages = [],
}) {
  const isEditMode = !!giveaway;
  const currentStatus = giveaway?.status || "draft";
  const { data: progress } = useGetGiveawayStats(giveaway?.id);
  const hasScans = progress?.totalParticipants > 0;

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const defaultValues = isEditMode
    ? {
        titleI18n: mergeI18nWithDefaults(
          giveaway?.titleI18n,
          mainLanguage,
          otherLanguages
        ),
        descriptionI18n: mergeI18nWithDefaults(
          giveaway?.descriptionI18n,
          mainLanguage,
          otherLanguages
        ),
        pageId: giveaway?.pageId ? String(giveaway.pageId) : null,
      }
    : {
        eventId: event?.id,
        titleI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
        descriptionI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
        pageId: null,
      };

  const schema = isEditMode ? web_updateGiveawaySchema : createGiveawaySchema;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields, isValid },
  } = useForm({
    defaultValues,
    resolver: resolver(schema),
    mode: "onChange",
  });

  const { mutate: createGiveaway, isPending: isCreating } = useCreateGiveaway();
  const { mutate: updateGiveaway, isPending: isUpdating } = useUpdateGiveaway(
    giveaway?.id
  );
  const { mutate: deleteGiveaway, isPending: isDeleting } = useDeleteGiveaway(
    giveaway?.id
  );
  const { mutate: updateGiveawayStatus, isPending: isChangingStatus } =
    useUpdateGiveawayStatus(giveaway?.id);

  const handleFormSubmit = (data) => {
    if (!isEditMode) {
      createGiveaway(data);
      reset(defaultValues);
      return;
    }
    const dirtyData = extractDirtyData(data, dirtyFields);

    if (Object.keys(dirtyData).length === 0) return;
    updateGiveaway(dirtyData);
    reset(data);
  };

  const statusBadge = getStatusBadgeProps(currentStatus);

  const statusOptions = Object.entries(STATUS_INFO).map(([value, info]) => ({
    value,
    label: info.label,
  }));

  const confirmDelete = useDeleteConfirm("giveaway", deleteGiveaway);
  const confirm = useConfirm();

  const handleStatusChange = (newStatus) => {
    if (!newStatus || newStatus === currentStatus) {
      return;
    }

    if (newStatus === "live" && !canGoLive) {
      return;
    }

    const warning = getTransitionWarning(currentStatus, newStatus, hasScans);

    if (warning) {
      confirm({
        title: warning.title,
        message: warning.message,
        onConfirm: () => updateGiveawayStatus({ status: newStatus }),
      });
    } else {
      updateGiveawayStatus({ status: newStatus });
    }
  };

  const canGoLive = giveaway?.sponsorIds?.length > 0;

  return (
    <Container py="md" size="xl">
      <Grid gap="lg">
        {isEditMode && (
          <Grid.Col span={4}>
            <Paper
              withBorder
              shadow="sm"
              p="lg"
              radius="md"
              style={{ position: "sticky", top: 20 }}
            >
              <Stack gap="lg">
                <Badge
                  color={statusBadge.color}
                  size="xl"
                  variant="filled"
                  fullWidth
                  style={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {statusBadge.label}
                </Badge>

                <Alert
                  icon={<IconInfoCircle size="1rem" />}
                  color={STATUS_INFO[currentStatus]?.color}
                  variant="light"
                  styles={{
                    message: { fontSize: "13px" },
                  }}
                >
                  {STATUS_INFO[currentStatus]?.description}
                </Alert>

                <Divider />

                <Box>
                  <MantineSelect
                    label="Change Status"
                    description="Changes apply immediately"
                    data={statusOptions}
                    value={currentStatus}
                    onChange={handleStatusChange}
                    disabled={isChangingStatus}
                    allowDeselect={false}
                  />
                  {!canGoLive && (
                    <Text c="orange" size="xs" mt="xs" fw={500}>
                      Add sponsors before going live
                    </Text>
                  )}
                </Box>

                <Divider />

                <Button
                  color="red"
                  variant="light"
                  disabled={currentStatus === "live"}
                  onClick={() =>
                    confirmDelete({ id: giveaway?.id, eventId: event?.id })
                  }
                  loading={isDeleting}
                  fullWidth
                >
                  Delete Giveaway
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>
        )}

        <Grid.Col span={isEditMode ? 8 : 12}>
          <Paper withBorder shadow="sm" p="lg" radius="md">
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <Stack gap="lg">
                <Title order={3} mb="xs">
                  {isEditMode ? "Edit Giveaway" : "Create Giveaway"}
                </Title>

                <Divider />

                <LocalizedInput
                  control={control}
                  fields={localizedInputFields}
                  mainLanguage={event?.language}
                  otherLanguages={event?.secondaryLanguages}
                  disabled={currentStatus === "closed"}
                />

                <Select
                  control={control}
                  name="pageId"
                  label="Page"
                  placeholder="Select page"
                  data={pages.map((page) => ({
                    value: String(page.id),
                    label: page.title,
                  }))}
                  clearable
                  disabled={currentStatus === "closed"}
                />

                <Button
                  type="submit"
                  loading={isCreating || isUpdating}
                  disabled={isEditMode ? !isDirty : !isValid}
                  size="md"
                  fullWidth
                >
                  {isEditMode ? "Save Changes" : "Create Giveaway"}
                </Button>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
